import { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useDrop } from "react-dnd";
import dayjs from "dayjs";
import { Typography } from "components/atoms";
import { Card } from "./Card";
import { Tooltip, TooltipTrigger, TooltipContent } from "components/molecules";
import "./drop-container.scss";

interface IProps {
  setModalState: any;
  day: any;
  formatDate: any;
  cardList: any;
  validateRescheduleDate: any;
  theme: "light" | "dark";
}

export const DropContainer = ({
  setModalState,
  day,
  formatDate,
  cardList,
  validateRescheduleDate,
  theme,
}: IProps) => {
  const [toggleViewMore, setToggleViewMore] = useState(false);
  const showMoreRef = useRef<HTMLDivElement>(null);

  const handleClick = (type: string, eventId: string, card: any) => {
    if (type === "TestDay" || type === "TestSegment") {
      return;
    } else if (type === "Prework") {
      setModalState((prev: any) => ({
        ...prev,
        type: "Prework",
        showModal: !prev.showModal,
        eventId: eventId,
        cardData: card
      }));
    } else if (type === "Test") {
      setModalState((prev: any) => ({
        ...prev,
        type: "Test",
        showModal: !prev.showModal,
        cardData: card
      }));
    } else if (type === "Custom") {
      setModalState((prev: any) => ({
        ...prev,
        type: "Custom",
        showModal: !prev.showModal,
        cardData: card
      }));
    } else if (type === "Diagnostic") {
      setModalState((prev: any) => ({
        ...prev,
        type: "Diagnostic",
        showModal: !prev.showModal,
        eventId: eventId,
        cardData: card
      }));
    } else if (type === "Homework") {
      setModalState((prev: any) => ({
        ...prev,
        type: "Homework",
        showModal: !prev.showModal,
        eventId: eventId,
        cardData: card
      }));
    } else {
      setModalState((prev: any) => ({
        ...prev,
        type: `${type}`,
        showModal: !prev.showModal,
        eventId: eventId,
        cardData: card
      }));
    }
  };

  //   const moveCard = useCallback(
  //     (dragIndex: any, hoverIndex: any, cardWeek: any, cardDayOfWeek: any) => {
  //         setCards((prevCards) => {
  //           return update(prevCards, {
  //             $splice: [
  //               [dragIndex, 1],
  //               [hoverIndex, 0, prevCards[dragIndex]],
  //             ],
  //           });
  //         });
  //     },
  //     []
  //   );

  const handleMoreClick = () => setToggleViewMore((prev) => !prev);

  const renderCard = useCallback(
    (card: any, day: any, index: any) => {
      return (
        <Tooltip key={index}>
          <TooltipTrigger>
            <Card
              key={index}
              index={index}
              id={card.id}
              text={card.Title}
              type={card.EventType}
              cardWeek={card.week}
              cardDayOfWeek={card.dayOfWeek}
              cardInfo={card}
              day={day}
              // moveCard={moveCard}
              onClick={() =>
                card.StudyPlannerEventId !== null &&
                handleClick(card.EventType, card.StudyPlannerEventId, card)
              }
              theme={theme}
            />
          </TooltipTrigger>
          <TooltipContent className={classNames(`tooltip-${theme}`,"tooltip-content")}>
            {card.EventType === "TestSegment"
              ? `${card.Title}`
              : card.EventType === "ClassSegment"
                ? `MCAT ${card.Title}`
                : card.EventType === "Diagnostic"
                  ? `MCAT ${card.Title} ${card.EventType} Exam`
                  : `MCAT ${card.Title} ${card.EventType}`}
          </TooltipContent>
        </Tooltip>
      );
    },
    [theme]
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (
      showMoreRef.current &&
      !showMoreRef.current.contains(event.target as Node)
    ) {
      setToggleViewMore(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const ref = useRef(null);
  const [{ canDrop, isOver }, drop] = useDrop({
    accept: "card",
    drop(item: any) {
      validateRescheduleDate(item.cardInfo, formatDate);
    },
    collect: (monitor: any) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  drop(ref);

  return (
    <div
      className={classNames({
        "drop-container": true,
        "active-drop": canDrop && isOver,
      })}
      ref={ref}
    >
      {day.inCurrentMonth && cardList
        ? cardList
            .filter(
              (val: any) => dayjs(val.Start).format("YYYY-MM-DD") === formatDate
            )
            .sort((first: any, second: any) => {
              if (first.EventOrder === 0) return 1;
              if (second.EventOrder === 0) return -1;
              else return first.EventOrder - second.EventOrder;
            })
            .slice(0, 3)
            .map((card: any, i: any) => renderCard(card, day, i))
        : null}
      {day.inCurrentMonth &&
      cardList &&
      cardList.filter(
        (val: any) => dayjs(val.Start).format("YYYY-MM-DD") === formatDate
      ).length > 3 ? (
        <div
          ref={showMoreRef}
          className="more-text-wrapper"
          onClick={handleMoreClick}
        >
          <Typography
            variant="h9Book"
            // label="+1 more"
            label={`+${
              day.inCurrentMonth &&
              cardList &&
              cardList.filter(
                (val: any) =>
                  dayjs(val.Start).format("YYYY-MM-DD") === formatDate
              ).length - 3
            } more`}
            styles="view-more-text-custom-style"
          />
          <div
            className={classNames({
              "more-container": true,
              "most-left-day": day.dateNumber.day() === 0,
              "most-right-day": day.dateNumber.day() === 6,
              show: toggleViewMore,
              "light-shadow": theme === "light",
              "dark-shadow": theme === "dark",
            })}
          >
            {cardList
              .filter(
                (val: any) =>
                  dayjs(val.Start).format("YYYY-MM-DD") === formatDate
              )
              .sort((first: any, second: any) => {
                if (first.EventOrder === 0) return 1;
                if (second.EventOrder === 0) return -1;
                else return first.EventOrder - second.EventOrder;
              })
              .map((card: any, i: any) => renderCard(card, day, i))}
          </div>
          {toggleViewMore ? (
            <svg className="triangle">
              <polygon
                className="outer-border"
                points="0,0 41,0 20.5,20 20.5,20"
                fill="currentColor"
              />
              <polygon
                className="inner-border"
                points="1,0 40,0 20.5,19 "
                fill="currentColor"
              />
            </svg>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
